export type UsageDimension =
  | "provider"
  | "job"
  | "run"
  | "task"
  | "customer"
  | "target"
  | "machine"
  | "device"
  | "model"
  | "key"
  | "mode"
  | "status";
export type UsageGranularity = "total" | "hour" | "day" | "week" | "month";
export type UsageMetric =
  | "costUsd"
  | "totalTokens"
  | "inputTokens"
  | "outputTokens"
  | "cachedTokens"
  | "calls";
export type UsageTotals = {
  calls: number;
  failed: number;
  inputTokens: number;
  cachedTokens: number;
  outputTokens: number;
  reasoningTokens: number;
  totalTokens: number;
  costUsd: number;
  unpriced: number;
};
export type UsagePart = {
  dim: UsageDimension;
  key: string | null;
  label: string;
};
export type UsageGroup = {
  id: string;
  label: string;
  parts: UsagePart[];
  total: UsageTotals;
};
export type UsageExploreView = {
  total: UsageTotals;
  groups: UsageGroup[];
  groupCount: number;
  chartGroups: { id: string; label: string }[];
  series: { period: string; values: Record<string, UsageTotals> }[];
};
export type UsageFilters = {
  provider?: string;
  customerId?: string;
  targetCustomerId?: string;
  machineId?: string;
  taskId?: string;
  jobId?: string;
  keyId?: string;
  mode?: string;
  model?: string;
  status?: string;
};
export type UsageExploreQuery = UsageFilters & {
  from: string;
  to: string;
  granularity: UsageGranularity;
  dims: UsageDimension[];
  metric: UsageMetric;
  top: 5 | 10 | 20;
};
export type UsageOption = { value: string; label: string };
export type UsageExploreOptions = Partial<
  Record<keyof UsageFilters, UsageOption[]>
>;
export type UsageExplorerExample =
  | "plan"
  | "normal"
  | "multi"
  | "total"
  | "hour"
  | "loading"
  | "error"
  | "empty"
  | "unpriced";
export const usageDimensions: Record<UsageDimension, string> = {
  provider: "공급자",
  job: "작업",
  run: "시도(재시도별)",
  task: "원고 작업",
  customer: "실행 고객사",
  target: "대상 고객사",
  machine: "머신",
  device: "디바이스",
  model: "모델",
  key: "API 키",
  mode: "방식",
  status: "상태",
};
export const usageMetrics: Record<UsageMetric, string> = {
  costUsd: "비용 (USD)",
  totalTokens: "총 토큰",
  inputTokens: "입력 토큰",
  outputTokens: "출력 토큰",
  cachedTokens: "캐시 입력",
  calls: "호출 수",
};
export const usageFilterLabels: Record<keyof UsageFilters, string> = {
  provider: "공급자",
  customerId: "고객사 (실행·대상)",
  targetCustomerId: "대상 고객사",
  machineId: "머신",
  taskId: "원고 작업",
  jobId: "특정 작업",
  keyId: "API 키",
  mode: "방식",
  model: "모델",
  status: "상태",
};
export const usageFilterForDimension: Partial<
  Record<UsageDimension, keyof UsageFilters>
> = {
  provider: "provider",
  job: "jobId",
  task: "taskId",
  customer: "customerId",
  target: "targetCustomerId",
  machine: "machineId",
  model: "model",
  key: "keyId",
  mode: "mode",
  status: "status",
};
export function kstDate(date = new Date()) {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Seoul",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(date);
}
export function shiftUsageDate(date: string, delta: number) {
  const d = new Date(date + "T00:00:00Z");
  d.setUTCDate(d.getUTCDate() + delta);
  return d.toISOString().slice(0, 10);
}
export function initialUsageQuery(today = kstDate()): UsageExploreQuery {
  return {
    from: shiftUsageDate(today, -6),
    to: today,
    granularity: "day",
    dims: ["job"],
    metric: "costUsd",
    top: 10,
  };
}
export function usageQueryError(query: UsageExploreQuery) {
  if (!query.from || !query.to) return "시작일과 종료일을 선택해 주세요.";
  if (query.from > query.to) return "종료일은 시작일 이후로 선택해 주세요.";
  if (
    query.granularity === "hour" &&
    (Date.parse(query.to) - Date.parse(query.from)) / 86400000 + 1 > 14
  )
    return "시간 단위는 14일 이내 기간에서만 볼 수 있습니다.";
  return "";
}

const customers = [
  { value: "onyou", label: "온유의원" },
  { value: "moru", label: "모루한의원" },
  { value: "bom", label: "봄약국" },
];
const jobKey = (i: number) =>
  ["JOB-021", "JOB-018", "JOB-019"][i] ?? `job-${i + 1}`;
const jobs = [
  "가을 진료 안내 발행",
  "검색 노출 지표 수집",
  "진료 시간 안내 발행",
  "눈 건강 정기 검진",
  "환절기 알레르기 관리",
  "수분 섭취와 건강",
  "치아 관리의 기본",
  "스트레스와 회복",
  "일상 속 가벼운 운동",
  "규칙적인 식사 습관",
  "기초 건강 체크",
  "면역력 관리 안내",
];
export const usageExploreOptions: UsageExploreOptions = {
  provider: [
    { value: "openai", label: "API 키" },
    { value: "codex", label: "GPT 플랜" },
  ],
  customerId: customers,
  targetCustomerId: customers,
  machineId: [
    { value: "machine-1", label: "온유 메인 PC" },
    { value: "machine-2", label: "모루 게시 PC" },
  ],
  jobId: jobs.map((label, i) => ({ value: jobKey(i), label })),
  model: [
    { value: "gpt-5", label: "gpt-5" },
    { value: "gpt-5-mini", label: "gpt-5-mini" },
  ],
  keyId: [
    { value: "key-main", label: "운영 기본 키" },
    { value: "key-backup", label: "보조 키" },
  ],
  mode: [
    { value: "relay", label: "API 키" },
    { value: "local", label: "로컬 세션" },
  ],
  status: [
    { value: "ok", label: "성공" },
    { value: "error", label: "실패" },
    { value: "cancelled", label: "취소" },
  ],
};
const zero = (): UsageTotals => ({
  calls: 0,
  failed: 0,
  inputTokens: 0,
  cachedTokens: 0,
  outputTokens: 0,
  reasoningTokens: 0,
  totalTokens: 0,
  costUsd: 0,
  unpriced: 0,
});
const add = (to: UsageTotals, from: UsageTotals) => {
  for (const key of Object.keys(to) as (keyof UsageTotals)[])
    to[key] += from[key];
};
/** Preview-only response simulator. Production UsageExplorer never derives totals or groups from calls. */
export function usageExploreFixture(
  query: UsageExploreQuery,
  empty = false,
  unpriced = false,
  includePlan = false,
): UsageExploreView {
  const total = zero();
  if (empty || usageQueryError(query))
    return { total, groups: [], groupCount: 0, chartGroups: [], series: [] };
  const rows: {
    period: string;
    parts: Record<UsageDimension, { key: string; label: string }>;
    metrics: UsageTotals;
  }[] = [];
  const days = Math.min(
    90,
    Math.floor((Date.parse(query.to) - Date.parse(query.from)) / 86400000) + 1,
  );
  for (let d = 0; d < days; d++) {
    const date = shiftUsageDate(query.from, d);
    for (let j = 0; j < jobs.length; j++) {
      const customer = j === 2 ? customers[0] : customers[j % 3],
        target = j < 3 ? customer : customers[(j + (j % 4 === 0 ? 1 : 0)) % 3],
        mode = j % 4 === 0 ? "local" : "relay",
        model = j % 2 ? "gpt-5-mini" : "gpt-5",
        status = j === 4 && d % 3 === 0 ? "error" : "ok";
      const provider = includePlan && j === 11 ? "codex" : "openai";
      const parts = {
        provider: {
          key: provider,
          label: provider === "codex" ? "GPT 플랜" : "API 키",
        },
        job: { key: jobKey(j), label: jobs[j] },
        run: {
          key: ["RUN-1048", "RUN-1047", "RUN-1046"][j] ?? `run-${j + 1}-${d}`,
          label: `${jobs[j]} · ${(d % 2) + 1}번째 시도`,
        },
        task: { key: `task-${j + 1}`, label: jobs[j] },
        customer: { key: customer.value, label: customer.label },
        target: { key: target.value, label: target.label },
        machine: {
          key: `machine-${(j % 2) + 1}`,
          label: j % 2 ? "모루 게시 PC" : "온유 메인 PC",
        },
        device: {
          key: `R5CT${j + 1200}`,
          label: `Galaxy S${23 + (j % 2)} · ${j + 1}`,
        },
        model: { key: model, label: model },
        key: {
          key: mode === "local" ? "" : j % 3 ? "key-main" : "key-backup",
          label:
            mode === "local" ? "로컬 세션" : j % 3 ? "운영 기본 키" : "보조 키",
        },
        mode: { key: mode, label: mode === "local" ? "로컬 세션" : "API 키" },
        status: { key: status, label: status === "ok" ? "성공" : "실패" },
      };
      if (
        query.customerId &&
        customer.value !== query.customerId &&
        target.value !== query.customerId
      )
        continue;
      const filterDims: [keyof UsageFilters, UsageDimension][] = [
        ["provider", "provider"],
        ["targetCustomerId", "target"],
        ["machineId", "machine"],
        ["taskId", "task"],
        ["jobId", "job"],
        ["keyId", "key"],
        ["mode", "mode"],
        ["model", "model"],
        ["status", "status"],
      ];
      if (
        filterDims.some(
          ([filter, dim]) => query[filter] && parts[dim].key !== query[filter],
        )
      )
        continue;
      let period = date;
      if (query.granularity === "total") period = "전체";
      if (query.granularity === "month") period = date.slice(0, 7);
      if (query.granularity === "week") {
        const dow = new Date(date + "T00:00:00Z").getUTCDay();
        period = shiftUsageDate(date, -(dow || 7) + 1);
      }
      if (query.granularity === "hour")
        period = `${date} ${String(9 + (j % 8)).padStart(2, "0")}:00`;
      const calls = Math.max(1, 16 - j + ((d + j) % 5)),
        inputTokens = calls * (6800 + j * 120),
        outputTokens = calls * (720 + j * 30),
        missingPrice = unpriced && j === 1;
      rows.push({
        period,
        parts,
        metrics: {
          calls,
          failed: status === "error" ? calls : 0,
          inputTokens,
          cachedTokens: calls * 1600,
          outputTokens,
          reasoningTokens: calls * 120,
          totalTokens: inputTokens + outputTokens,
          costUsd:
            provider === "codex"
              ? 0
              : missingPrice
                ? 0
                : Number((calls * (0.32 - j * 0.018)).toFixed(4)),
          unpriced: missingPrice ? calls : 0,
        },
      });
    }
  }
  const grouped = new Map<string, UsageGroup>();
  for (const row of rows) {
    const parts = query.dims.map((dim) => ({ dim, ...row.parts[dim] })),
      id = parts.map((p) => p.key).join("|");
    let group = grouped.get(id);
    if (!group) {
      group = {
        id,
        parts,
        label: parts.map((p) => p.label).join(" · ") || "전체",
        total: zero(),
      };
      grouped.set(id, group);
    }
    add(group.total, row.metrics);
    add(total, row.metrics);
  }
  const groups = [...grouped.values()].sort(
    (a, b) => b.total[query.metric] - a.total[query.metric],
  );
  const chartGroups = groups
    .slice(0, query.top)
    .map(({ id, label }) => ({ id, label }));
  const top = new Set(chartGroups.map((g) => g.id));
  if (groups.length > query.top)
    chartGroups.push({
      id: "__other__",
      label: `그 외 ${groups.length - query.top}개`,
    });
  const periods = new Map<string, Record<string, UsageTotals>>();
  for (const row of rows) {
    const id = query.dims.map((dim) => row.parts[dim].key).join("|"),
      chartId = top.has(id) ? id : "__other__",
      values = periods.get(row.period) ?? {};
    periods.set(row.period, values);
    values[chartId] ??= zero();
    add(values[chartId], row.metrics);
  }
  return {
    total,
    groups: groups.slice(0, 500),
    groupCount: groups.length,
    chartGroups,
    series: [...periods.entries()]
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([period, values]) => ({ period, values })),
  };
}
